import { useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { payApi } from "../../../api/pay";
import { Modal } from "../../../components/Overlay";
import { Button, ErrorText, Field, TextInput } from "../../../components/ui";

const area = "w-full rounded-sm border border-white/15 bg-surface p-3 text-sm text-white";

function useSubmit(fn: () => Promise<unknown>, ok: string, onDone: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      toast.success(ok);
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

export function ApproveModal({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [ack, setAck] = useState(false);
  const s = useSubmit(() => payApi.approve(id, note, ack), "Policy business-approved", onDone);
  return (
    <Modal open title="Approve policy (business)" onClose={onClose} testId="policy-approve-modal">
      <div className="space-y-4 text-sm">
        <Field label="Approval note (required)" htmlFor="policy-approve-note">
          <textarea id="policy-approve-note" rows={3} className={area} value={note} onChange={(e) => setNote(e.target.value)} data-testid="policy-approve-note" />
        </Field>
        <label className="flex items-start gap-2 text-xs text-zinc-300">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#ff1d00]" data-testid="policy-approve-ack" />
          I understand final compliance validation by a qualified payroll advisor is still required
        </label>
        <ErrorText message={s.error} testId="policy-approve-error" />
        <Button onClick={s.run} loading={s.busy} disabled={!note.trim() || !ack} data-testid="policy-approve-submit">Approve</Button>
      </div>
    </Modal>
  );
}

export function AdvisorModal({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState("");
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const s = useSubmit(() => payApi.advisor(id, name, on, note), "Advisor validation recorded", onDone);
  return (
    <Modal open title="Record advisor validation" onClose={onClose} testId="policy-advisor-modal">
      <div className="space-y-4 text-sm">
        <Field label="Advisor name" htmlFor="advisor-name"><TextInput id="advisor-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="advisor-name-input" /></Field>
        <Field label="Validation date" htmlFor="advisor-date"><TextInput id="advisor-date" type="date" value={on} onChange={(e) => setOn(e.target.value)} data-testid="advisor-date-input" /></Field>
        <Field label="Note" htmlFor="advisor-note"><textarea id="advisor-note" rows={3} className={area} value={note} onChange={(e) => setNote(e.target.value)} data-testid="advisor-note-input" /></Field>
        <ErrorText message={s.error} testId="advisor-error" />
        <Button onClick={s.run} loading={s.busy} disabled={!name.trim() || !note.trim()} data-testid="advisor-submit">Record</Button>
      </div>
    </Modal>
  );
}
